import React, { useMemo } from 'react';
import { Modal, Button, Card } from '@/components/Bicester';
import { Cell } from '@/components/Bicester/Table';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import Image from '@/components/Bicester/Image';
import { Alert, IconButton } from 'rsuite';
import { ORDER_STATUS } from './constants';
import { FieldArray, FormikProvider, useFormik } from 'formik';
import { Form, InputNumberField, FormField, SelectPicker } from '@/components/formik-rsuite';
import { registerModalSchema } from './constants';
import { usePersistFn, useRequest } from 'ahooks';
import { fetchBags, fetchAddBags } from '@/services/subscribe/shopping';
import { SERVICE_STATUS } from '@/services/constants';
import useRouter from '@/hooks/useRouter';
import { getDetailPath, SERVICE_LOG_DETAIL } from '../constants';
import useSiteBrandList from '@/hooks/preload/useSiteBrandList';

export const RegisterModal = NiceModal.create(({ rowData, onRefresh }: any) => {
  const modal = useModal();
  const { id: handsfree_id } = rowData;
  const { data, brandList, isLoading } = useRegisterData(rowData);

  const onSubmit = usePersistFn(async (values) => {
    const { status } = await fetchAddBags({
      handsfree_id,
      bags: JSON.stringify(values.shopping_bag),
    });
    if (status === SERVICE_STATUS.SUCCESS) {
      modal.remove();
      Alert.success('登记成功');
      onRefresh();
    }
  });

  const formik = useFormik({
    initialValues: { shopping_bag: data && data?.length ? data : [{ brand_name: '', num: '1' }] },
    validationSchema: registerModalSchema,
    enableReinitialize: true,
    onSubmit,
  });

  return (
    <Modal
      isInput
      loading={isLoading}
      content={
        <>
          <FormikProvider value={formik}>
            <Form>
              <FieldArray name="shopping_bag">
                {({ push, remove }) => (
                  <>
                    <div className="flex items-center">
                      <IconButton
                        className="flex items-center"
                        onClick={() => push({ brand_name: '', num: '1' })}
                      >
                        <Image src="/resources/images/add.svg" />
                        <div className="text-[12px] text-[#999999] ml-[5px]">新增购物袋</div>
                      </IconButton>
                      <div className="text-[12px] text-[#999999] ml-[20px]">
                        购物袋总数{' '}
                        {eval(formik?.values?.shopping_bag?.map((bag) => bag?.num || 0)?.join('+'))}
                      </div>
                    </div>
                    <Card
                      smDivider
                      className="register-modal-form max-h-[400px] overflow-y-auto py-[30px]"
                    >
                      {formik?.values?.shopping_bag?.map((item, index) => (
                        <div key={index} className="flex" style={{ marginBottom: 10 }}>
                          <FormField
                            className="w-[70%]"
                            accepter={SelectPicker}
                            label="品牌名"
                            labelStyle={{
                              width: 0,
                              marginLeft: 0,
                            }}
                            name={`shopping_bag.${index}.brand_name`}
                            data={brandList}
                            menuMaxHeight={180}
                            inline
                          />
                          <FormField
                            accepter={InputNumberField}
                            name={`shopping_bag.${index}.num`}
                            label="购物袋数量"
                            style={{ width: 180 }}
                            labelStyle={{ flex: 2, whiteSpace: 'nowrap' }}
                            controls={false}
                            inline
                          />
                          <Button
                            className={`${
                              formik.values.shopping_bag.length <= 1 ? 'invisible' : ''
                            }`}
                            appearance="link"
                            onClick={() => remove(index)}
                            style={{ color: '#73D8DA' }}
                          >
                            删除
                          </Button>
                        </div>
                      ))}
                    </Card>
                  </>
                )}
              </FieldArray>
            </Form>
          </FormikProvider>
        </>
      }
      show={modal.visible}
      onCancel={modal.remove}
      cancel="不保存"
      confirm="保存"
      onConfirm={formik.submitForm}
    />
  );
});

const RegisterCell = ({ rowData, ...props }: any) => {
  const { history } = useRouter();
  return (
    <>
      <Cell {...props}>
        {rowData?.status !== ORDER_STATUS.COMPLTED ? (
          rowData?.status !== ORDER_STATUS.CANCEL && (
            <Button appearance="link" onClick={() => NiceModal.show('register-modal', { rowData })}>
              开始登记
            </Button>
          )
        ) : (
          <span
            className="cursor-pointer"
            onClick={() => {
              history.push(`${getDetailPath(SERVICE_LOG_DETAIL.SHOPING)}/${rowData?.service_id}`);
            }}
          >
            查看服务记录
          </span>
        )}
      </Cell>
    </>
  );
};

function useRegisterData(rowData) {
  // 获取之前的购物袋
  const { data, loading } = useRequest<any>(fetchBags, {
    defaultParams: { handsfree_id: rowData?.id },
  });

  // 获取品牌列表
  const { data: brandList, loading: brandListLoading } = useSiteBrandList();

  const isLoading = useMemo(() => loading || brandListLoading, [loading, brandListLoading]);

  return { data, brandList, isLoading };
}

export default RegisterCell;
